import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import Logo from '../Logo/Logo';
import { useLanguage } from '../../context/LanguageContext';
import {
  HomeIcon,
  CogIcon,
  UserIcon,
  PhoneIcon,
  ChartBarIcon,
  AcademicCapIcon,
  CreditCardIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  UserCircleIcon,
  GlobeAltIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { sidebarOpen, setSidebarOpen, notifications, fetchNotifications, fetchUnreadCount, markNotificationRead, markAllNotificationsRead, agents } = useAppStore();
  const { currentLanguage, t, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const dropdownRef = useRef(null);
  const langRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (langRef.current && !langRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count on mount and poll every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleBellClick = useCallback(() => {
    setShowNotifDropdown((prev) => {
      if (!prev) fetchNotifications();
      return !prev;
    });
  }, [fetchNotifications]);

  const handleNotifClick = useCallback((notif) => {
    if (!notif.is_read) markNotificationRead(notif.id);
  }, [markNotificationRead]);

  const typeColor = (type) => {
    if (type === 'billing') return 'bg-green-500/10 text-green-400 border-green-500/20';
    if (type === 'error') return 'bg-red-500/10 text-red-400 border-red-500/20';
    if (type === 'warning') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  };

  const pages = [
    { name: t('dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('voiceAgents'), href: '/agents', icon: UserIcon },
    { name: t('createVoiceAgent') || 'Create Voice Agent', href: '/agents/create', icon: UserIcon },
    { name: t('analytics'), href: '/analytics', icon: ChartBarIcon },
    { name: t('training'), href: '/training', icon: AcademicCapIcon },
    { name: t('batchCalling') || 'Batch Calling', href: '/batchcallinglist', icon: Bars3Icon },
    { name: t('callLogs'), href: '/call-logs', icon: PhoneIcon },
    { name: t('billing'), href: '/billing', icon: CreditCardIcon },
    { name: t('settings'), href: '/settings', icon: CogIcon },
  ];

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const matchedPages = pages
      .filter((p) => p.name.toLowerCase().includes(q))
      .map((p) => ({ type: 'page', label: p.name, href: p.href, Icon: p.icon }));
    const agentList = Array.isArray(agents) ? agents : [];
    const matchedAgents = agentList
      .filter((a) => (a.agent_name || a.name || '').toLowerCase().includes(q))
      .slice(0, 5)
      .map((a) => ({ type: 'agent', label: a.agent_name || a.name, href: '/agents', Icon: UserIcon }));
    return [...matchedPages, ...matchedAgents].slice(0, 8);
  }, [searchQuery, agents]);

  const handleSearchSelect = (href) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate(href);
  };

  const formatNotifTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  const normalizedRole = user?.role?.toLowerCase().replace(/\s/g, '');

  const navigation = [
    { name: t('dashboard'), href: '/dashboard', icon: HomeIcon },
    { name: t('voiceAgents'), href: '/agents', icon: UserIcon },
    ...(normalizedRole === 'superadmin' ? [{ name: t('users'), href: '/user', icon: UserIcon }] : []),
    { name: t('analytics'), href: '/analytics', icon: ChartBarIcon },
    { name: t('training'), href: '/training', icon: AcademicCapIcon },
    { name: t('batchCalling'), href: '/batchcallinglist', icon: Bars3Icon },
    { name: t('callLogs'), href: '/call-logs', icon: PhoneIcon },
    { name: t('billing'), href: '/billing', icon: CreditCardIcon },
    { name: t('settings'), href: '/settings', icon: CogIcon },
  ];

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return t('dashboard');
    if (path === '/agents') return t('voiceAgents');
    if (path === '/agents/create') return t('createVoiceAgent');
    if (path.match(/\/agents\/.*\/edit/)) return t('editVoiceAgent');
    if (path.match(/\/agents\/.*\/retrain/)) return t('retrainAgent');
    if (path === '/analytics') return t('performanceAnalytics');
    if (path === '/training') return t('agentTraining');
    if (path === '/batchcallinglist') return t('batchHistory');
    if (path === '/batchcalling/batchcallingform') return t('createBatchCall');
    if (path === '/call-logs') return t('callLogs');
    if (path === '/billing') return t('subscriptionBilling');
    if (path === '/settings') return t('accountSettings');
    if (path === '/profile') return t('userProfile');
    if (path === '/notifications') return t('notifications');
    if (path === '/user') return t('userManagement');
    return ''; // No title for unknown paths
  };

  const pageTitle = getPageTitle();

  const isCurrentPage = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-transparent">
      {/* Sidebar - Solid Integrated Glass */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 controlled-glass border-r border-white/10 transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 lg:static flex flex-col h-full`}
      >
        {/* Brand Section */}
        <div className="px-8 py-10">
          <Link to="/dashboard" className="flex items-center">
            <h1 className="text-2xl font-bold text-white tracking-[0.2em] uppercase">
              Valoria
            </h1>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto custom-scrollbar">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`sidebar-item group ${isCurrentPage(item.href) ? 'sidebar-item-active' : 'sidebar-item-inactive'
                }`}
            >
              <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 transition-grayscale ${isCurrentPage(item.href) ? 'text-white' : 'text-white/40 group-hover:text-white'
                }`} />
              <span className="truncate">{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* Action Bottom */}
        <div className="p-4 mt-auto">
          <button
            onClick={logout}
            className="flex items-center w-full px-4 py-3 text-sm font-medium rounded-xl text-white/40 hover:text-white hover:bg-white/5 transition-all group"
          >
            <svg className="mr-3 h-5 w-5 rotate-180 text-white/40 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Workspace */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative">
        {/* Minimal Header */}
        <header className="h-16 flex items-center justify-between px-4 sm:px-8 z-40 border-b border-white/5 bg-[#0a0f1d]/80 backdrop-blur-md">
          <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
            <button
              type="button"
              className="lg:hidden -ml-1 p-2 text-white/40 hover:text-white transition-colors flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            {pageTitle && (
              <h1 className="text-base sm:text-xl font-bold text-white tracking-tight animate-in fade-in slide-in-from-left-4 duration-500 truncate min-w-0">
                {pageTitle}
              </h1>
            )}
          </div>

          {/* Justified Search */}
          <div className="hidden lg:block flex-1 max-w-lg mx-6" ref={searchRef}>
            <div className="relative group">
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
                onFocus={() => { if (searchQuery) setShowSearchResults(true); }}
                onKeyDown={(e) => { if (e.key === 'Escape') { setShowSearchResults(false); setSearchQuery(''); } }}
                className="w-full bg-white/5 border border-white/10 rounded-xl py-2 px-5 text-sm text-white placeholder-white/20 focus:outline-none focus:bg-white/10 focus:border-white/20 transition-all pr-10"
              />
              <MagnifyingGlassIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20 group-focus-within:text-white transition-colors" />

              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 rounded-xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                  {searchResults.map((result, i) => (
                    <button
                      key={i}
                      onMouseDown={() => handleSearchSelect(result.href)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                    >
                      <result.Icon className="h-4 w-4 text-white/30 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{result.label}</span>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-white/20">{result.type}</span>
                    </button>
                  ))}
                </div>
              )}

              {showSearchResults && searchQuery.trim() && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 rounded-xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden">
                  <p className="px-4 py-3 text-xs text-white/20 italic">No results for "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            <div className="relative" ref={notifRef}>
              <button
                onClick={handleBellClick}
                className="p-2 text-white/60 hover:text-white transition-colors relative group"
                title={t('notifications')}
              >
                <BellIcon className="h-5 w-5" />
                {notifications.unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-0.5 bg-blue-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center shadow-[0_0_8px_rgba(59,130,246,0.5)]">
                    {notifications.unreadCount > 99 ? '99+' : notifications.unreadCount}
                  </span>
                )}
              </button>

              {showNotifDropdown && (
                <div className="absolute right-0 mt-2 w-80 rounded-2xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <span className="text-xs font-bold text-white uppercase tracking-widest">{t('notifications')}</span>
                    {notifications.unreadCount > 0 && (
                      <button
                        onClick={markAllNotificationsRead}
                        className="flex items-center gap-1 text-[10px] font-bold text-primary-400 hover:text-primary-300 uppercase tracking-widest transition-colors"
                      >
                        <CheckIcon className="h-3 w-3" />
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto custom-scrollbar">
                    {notifications.loading ? (
                      <div className="p-4 space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="animate-pulse flex gap-3">
                            <div className="w-2 h-2 rounded-full bg-white/10 mt-1.5 flex-shrink-0" />
                            <div className="flex-1 space-y-1.5">
                              <div className="h-3 w-32 bg-white/10 rounded" />
                              <div className="h-2.5 w-48 bg-white/5 rounded" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : notifications.items.length === 0 ? (
                      <div className="py-10 text-center">
                        <BellIcon className="h-8 w-8 text-white/10 mx-auto mb-2" />
                        <p className="text-xs font-bold text-white/20 uppercase tracking-widest">No notifications</p>
                      </div>
                    ) : (
                      notifications.items.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => handleNotifClick(notif)}
                          className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors flex gap-3 items-start ${!notif.is_read ? 'bg-white/[0.03]' : ''}`}
                        >
                          <span className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full border ${notif.is_read ? 'bg-transparent border-white/10' : 'bg-blue-500 border-blue-500/50 shadow-[0_0_6px_rgba(59,130,246,0.5)]'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-xs font-bold leading-snug ${notif.is_read ? 'text-white/50' : 'text-white'}`}>
                                {notif.title}
                              </p>
                              <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${typeColor(notif.notification_type)}`}>
                                {notif.notification_type}
                              </span>
                            </div>
                            {notif.body && (
                              <p className="text-[11px] text-white/30 mt-0.5 leading-snug line-clamp-2">{notif.body}</p>
                            )}
                            <p className="text-[10px] text-white/20 mt-1">{formatNotifTime(notif.created_at)}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="hidden sm:block h-4 w-[1px] bg-white/10 mx-1 sm:mx-2"></div>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center gap-2 p-2 text-white/60 hover:text-white transition-colors group"
                title="Change Language"
              >
                <GlobeAltIcon className="h-5 w-5" />
                <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:block">
                  {currentLanguage}
                </span>
              </button>

              {showLangDropdown && (
                <div className="absolute right-0 mt-2 w-32 rounded-xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-1">
                    <button
                      onClick={() => {
                        toggleLanguage('EN');
                        setShowLangDropdown(false);
                      }}
                      className={`flex items-center w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors whitespace-nowrap ${currentLanguage === 'EN' ? 'bg-primary-600/20 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                    >
                      English
                    </button>
                    <button
                      onClick={() => {
                        toggleLanguage('FR');
                        setShowLangDropdown(false);
                      }}
                      className={`flex items-center w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors whitespace-nowrap ${currentLanguage === 'FR' ? 'bg-primary-600/20 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                    >
                      Français
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden sm:block h-4 w-[1px] bg-white/10 mx-1 sm:mx-2"></div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-2 sm:gap-3 px-1.5 sm:px-3 py-1.5 sm:py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold text-sm shadow-[0_0_15px_rgba(37,99,235,0.3)]">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-bold text-white leading-none whitespace-nowrap">
                    {user?.name || 'User'}
                  </p>
                </div>
                <ChevronDownIcon className={`h-4 w-4 text-white/40 group-hover:text-white transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-white/10 bg-[#0a0f1d] backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-2 space-y-1">
                    <Link
                      to="/profile"
                      onClick={() => setShowProfileDropdown(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-colors group whitespace-nowrap"
                    >
                      <UserCircleIcon className="h-5 w-5 text-white/20 group-hover:text-white" />
                      <span>{t('profileSettings')}</span>
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setShowProfileDropdown(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-colors group whitespace-nowrap"
                    >
                      <CogIcon className="h-5 w-5 text-white/20 group-hover:text-white" />
                      <span>{t('account')}</span>
                    </Link>
                    <div className="h-[1px] bg-white/5 mx-2 my-1"></div>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        logout();
                      }}
                      className="flex items-center w-full gap-3 px-4 py-3 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors group whitespace-nowrap"
                    >
                      <svg className="h-5 w-5 text-red-400/50 group-hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>{t('signOut')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Dashboard Viewport */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 custom-scrollbar bg-white/[0.02]">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#0a0f1d]/60 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;